import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { fetchMe } from '../lib/api';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { connectionErrorMessage, getErrorStatus } from './authErrors';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: false,
  });
  const [mode, setMode] = useState<'login' | 'signup'>(() =>
    window.location.pathname.startsWith('/signup') ? 'signup' : 'login',
  );

  if (isLoading) {
    return (
      <div className="auth-loading" role="status">
        Checking session…
      </div>
    );
  }

  if (isError || !data) {
    const sessionMessage = isError ? sessionCheckMessage(error) : null;
    return (
      <div className="auth-signed-out">
        {sessionMessage && (
          <div className="login-form-error" role="alert" aria-live="assertive">
            {sessionMessage}
          </div>
        )}
        {mode === 'login' ? (
          <LoginForm onSwitchToSignup={() => setMode('signup')} />
        ) : (
          <SignupForm onSwitchToLogin={() => setMode('login')} />
        )}
      </div>
    );
  }

  return <AuthenticatedShell user={data}>{children}</AuthenticatedShell>;
}

function sessionCheckMessage(error: unknown): string | null {
  const status = getErrorStatus(error);
  if (status === 401) return null;
  if (status === 403) {
    return "Your session can't be used right now. Sign in again below, or contact support if this keeps happening.";
  }
  if (status === null) return connectionErrorMessage();
  if (status >= 500) {
    return "We couldn't check your existing session because the server had a problem. Sign in below, or try again in a minute.";
  }
  return "We couldn't check your existing session. Sign in below, or try again in a minute.";
}

interface AuthenticatedShellProps {
  user: { display_name: string; email: string };
  children: ReactNode;
}

function AuthenticatedShell({ user, children }: AuthenticatedShellProps) {
  return (
    <div className="auth-shell" data-user-email={user.email}>
      {children}
    </div>
  );
}
